import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCommunityImpact } from "@/hooks/use-queries";
import { useAnimatedCounter } from "@/hooks/use-animated-counter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DollarSign,
  Users,
  Heart,
  TrendingUp,
  Target,
  Clock,
  HandHeart,
  CreditCard,
} from "lucide-react";

interface CommunityImpactData {
  totalFundsRaised: number;
  familiesHelped: number;
  familyWallets: number;
  totalContributors: number;
  totalTransactions: number;
  communityFunds: number;
  walletsReachedGoal: number;
  avgGoalDays: number | null;
  totalPaymentPlans: number;
  activePaymentPlans: number;
  completedPaymentPlans: number;
}

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

function AnimatedStat({
  value,
  prefix = "",
  suffix = "",
  label,
  icon: Icon,
  iconColor,
  iconBg,
  animate,
  decimals = 0,
  delay = 0,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  icon: typeof DollarSign;
  iconColor: string;
  iconBg: string;
  animate: boolean;
  decimals?: number;
  delay?: number;
}) {
  const animatedValue = useAnimatedCounter(animate ? value : 0, {
    duration: 1200,
    delay,
    decimals,
  });

  const displayValue = decimals > 0
    ? animatedValue.toFixed(decimals)
    : Math.round(animatedValue).toLocaleString();

  return (
    <div className="flex flex-col items-center text-center p-4 sm:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${iconBg} mb-3`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
      <div className="text-2xl sm:text-3xl font-bold mb-1 tabular-nums" data-testid={`stat-value-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {prefix}{displayValue}{suffix}
      </div>
      <div className="text-sm text-muted-foreground font-medium">{label}</div>
    </div>
  );
}

function ImpactBar({
  current,
  total,
  label,
  animate,
  delay = 0,
}: {
  current: number;
  total: number;
  label: string;
  animate: boolean;
  delay?: number;
}) {
  const percentage = total > 0 ? Math.min((current / total) * 100, 100) : 0;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!animate) return;
    const timer = setTimeout(() => setWidth(percentage), delay);
    return () => clearTimeout(timer);
  }, [animate, percentage, delay]);

  return (
    <div className="space-y-1.5" data-testid={`impact-bar-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{current} / {total}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all duration-1000 ease-out"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export function CommunityImpactSection({ variant = "landing" }: { variant?: "landing" | "hub" }) {
  const { ref, isInView } = useInView(0.15);
  const { t } = useTranslation();

  const { data: impact, isLoading } = useCommunityImpact({
    staleTime: 60000,
  });

  const totalFundsCents = impact?.totalFundsRaised ?? 0;
  const totalFundsDollars = totalFundsCents / 100;

  const statsReady = isInView && !isLoading && !!impact;

  if (variant === "hub") {
    return (
      <Card data-testid="card-community-impact-hub">
        <CardContent className="p-5 sm:p-6" ref={ref}>
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">{t('community-impact.heading')}</h3>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="text-center space-y-2">
                    <Skeleton className="h-10 w-20 mx-auto" />
                    <Skeleton className="h-4 w-24 mx-auto" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="text-center p-3 rounded-lg bg-emerald-500/10" data-testid="hub-stat-raised">
                  <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    ${totalFundsDollars >= 1000 ? `${(totalFundsDollars / 1000).toFixed(1)}K` : totalFundsDollars.toFixed(0)}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('community-impact.stat-total-raised')}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-blue-500/10" data-testid="hub-stat-helped">
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                    {impact?.familiesHelped ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('community-impact.stat-wallets-funded')}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-primary/10" data-testid="hub-stat-contributors">
                  <div className="text-xl font-bold text-primary tabular-nums">
                    {impact?.totalContributors ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('community-impact.stat-contributors')}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-amber-500/10" data-testid="hub-stat-goals">
                  <div className="text-xl font-bold text-amber-600 dark:text-amber-400 tabular-nums">
                    {impact?.walletsReachedGoal ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground">{t('community-impact.stat-goals-reached')}</div>
                </div>
              </div>

              {(impact?.walletsReachedGoal ?? 0) > 0 && (impact?.familiesHelped ?? 0) > 0 && (
                <ImpactBar
                  current={impact?.walletsReachedGoal ?? 0}
                  total={impact?.familiesHelped ?? 0}
                  label={t('community-impact.label-goal-completion-rate')}
                  animate={statsReady}
                  delay={400}
                />
              )}

              {impact?.avgGoalDays && (
                <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-muted/50" data-testid="hub-stat-avg-days">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t('community-impact.avg-days-text', { days: impact.avgGoalDays })}
                  </span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <section
      ref={ref}
      className="py-12 sm:py-20 px-4 sm:px-6 bg-muted/30"
      data-testid="section-community-impact"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('community-impact.badge-real-results')}
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" data-testid="text-community-impact-title">
            {t('community-impact.heading')}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('community-impact.landing-desc')}
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {[0, 1, 2, 3].map(i => (
              <Card key={i}>
                <CardContent className="p-5 sm:p-6 text-center space-y-3">
                  <Skeleton className="h-12 w-12 rounded-full mx-auto" />
                  <Skeleton className="h-8 w-24 mx-auto" />
                  <Skeleton className="h-4 w-28 mx-auto" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
              <Card>
                <CardContent className="p-0">
                  <AnimatedStat
                    value={totalFundsDollars}
                    prefix="$"
                    label={t('community-impact.label-total-funds-raised')}
                    icon={DollarSign}
                    iconColor="text-emerald-600 dark:text-emerald-400"
                    iconBg="bg-emerald-500/10"
                    animate={statsReady}
                    decimals={0}
                    delay={0}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-0">
                  <AnimatedStat
                    value={impact?.familiesHelped ?? 0}
                    label={t('community-impact.label-families-helped')}
                    icon={Users}
                    iconColor="text-blue-600 dark:text-blue-400"
                    iconBg="bg-blue-500/10"
                    animate={statsReady}
                    delay={150}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-0">
                  <AnimatedStat
                    value={impact?.totalContributors ?? 0}
                    label={t('community-impact.stat-contributors')}
                    icon={HandHeart}
                    iconColor="text-primary"
                    iconBg="bg-primary/10"
                    animate={statsReady}
                    delay={300}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-0">
                  <AnimatedStat
                    value={impact?.communityFunds ?? 0}
                    label={t('community-impact.label-community-funds')}
                    icon={Heart}
                    iconColor="text-rose-600 dark:text-rose-400"
                    iconBg="bg-rose-500/10"
                    animate={statsReady}
                    delay={450}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <Card>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold">{t('community-impact.label-goal-achievement')}</span>
                  </div>
                  {(impact?.familiesHelped ?? 0) > 0 ? (
                    <ImpactBar
                      current={impact?.walletsReachedGoal ?? 0}
                      total={impact?.familiesHelped ?? 0}
                      label={t('community-impact.label-wallets-reaching-goal')}
                      animate={statsReady}
                      delay={600}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('community-impact.no-goal-placeholder')}</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold">{t('community-impact.label-avg-time-to-goal')}</span>
                  </div>
                  {impact?.avgGoalDays ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-primary tabular-nums" data-testid="stat-value-avg-goal-days">
                        {impact.avgGoalDays}
                      </span>
                      <span className="text-muted-foreground">{t('community-impact.days')}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('community-impact.no-avg-days-placeholder')}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('community-impact.from-creation-to-funded')}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                    <span className="font-semibold">{t('community-impact.label-payment-schedules')}</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-primary tabular-nums" data-testid="stat-value-payment-plans">
                      {impact?.totalPaymentPlans ?? 0}
                    </span>
                    <span className="text-muted-foreground">{t('community-impact.total')}</span>
                  </div>
                  {(impact?.activePaymentPlans ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('community-impact.currently-active', { count: impact?.activePaymentPlans })}
                    </p>
                  )}
                  {(impact?.completedPaymentPlans ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('community-impact.plans-paid-off', { count: impact?.completedPaymentPlans })}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('community-impact.helping-families')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
